import { Alert, PlainButton, Tooltip } from "@jugl-web/ui-components";
import { TextAreaInput } from "@jugl-web/ui-components/cross-platform/forms/TextAreaInput";
import { cx, useToast, useTranslations } from "@jugl-web/utils";
import React, { useCallback, useEffect, useState, ReactNode } from "react";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { DEFAULT_ESCALATE_TO_HUMAN_MESSAGE } from "@web-src/modules/cx/pages/CXAIAgentsPage/consts";
import { ReactComponent as ResetIcon } from "./assets/reset.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";

export const EscalateToHumanPanel: React.FC<{ entityId: string }> = ({
  entityId,
}) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { integrationsApi } = useRestApiProvider();
  const [message, setMessage] = useState("");
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const { data } = integrationsApi.useGetAiAgentConfigQuery({ entityId });
  const [updateAgentConfig] = integrationsApi.useUpdateAgentConfigMutation();
  const isDefaultMessage =
    message.trim() === DEFAULT_ESCALATE_TO_HUMAN_MESSAGE.trim();

  useEffect(() => {
    setMessage(data?.config.when_to_handover || "");
  }, [data]);

  const persistEscalateConfig = useCallback(
    async (nextMessage: string) => {
      const trimmedMessage = nextMessage.trim();
      setMessage(trimmedMessage);

      const isDirty = trimmedMessage !== data?.config.when_to_handover;
      if (!isDirty || !data) return;

      try {
        await updateAgentConfig({
          entityId,
          config: {
            ...data.config,
            when_to_handover: trimmedMessage,
          },
        }).unwrap();
      } catch {
        toast(
          t({
            id: "feedback.something-went-wrong",
            defaultMessage: "Something went wrong",
          }),
          { variant: "error" }
        );
      }
    },
    [data, entityId, t, toast, updateAgentConfig]
  );

  return (
    <div className="mx-8 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <span className="font-secondary font-medium text-black">
          {t({
            id: "cx-ai-agents-page.escalate-to-human-section",
            defaultMessage: "🔔 Escalate to Human",
          })}
        </span>
        {!isDefaultMessage && (
          <Tooltip
            renderTrigger={({ props, ref }) => (
              <PlainButton
                className="border-grey-400 hover:border-grey-600 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-solid transition-colors"
                ref={ref}
                {...props}
                onClick={() => setIsResetConfirmOpen(true)}
              >
                <ResetIcon />
              </PlainButton>
            )}
          >
            {t({
              id: "cx-ai-agents-page.reset-to-default-prompt",
              defaultMessage: "Reset to default prompt",
            })}
          </Tooltip>
        )}
      </div>
      <TextAreaInput
        value={message}
        onChange={(e) => setMessage(e.target.value.trimStart())}
        onBlur={() => persistEscalateConfig(message)}
        placeholder={t({
          id: "cx-ai-agents-page.escalate-to-human-placeholder",
          defaultMessage:
            "When the user asks to connect with a human representative, Before handing over to human representative, if you identify any opportunity to collect lead information such as name, email, phone number, etc. then you should collect the lead information.",
        })}
        classNames={{
          inputClassName: cx(
            "font-secondary text-sm leading-[140%] text-black jugl__custom-scrollbar h-[200px] min-h-[200px] max-h-[200px]"
          ),
        }}
      />
      <Alert
        isOpen={isResetConfirmOpen}
        onRequestClose={() => setIsResetConfirmOpen(false)}
        title={t({
          id: "cx-ai-agents-page.reset-escalate-to-human-dialog-title",
          defaultMessage: "Reset to default",
        })}
        content={t(
          {
            id: "cx-ai-agents-page.reset-escalate-to-human-dialog-description",
            defaultMessage:
              "Are you sure you want to reset to the default <highlightedText>Escalate to Human</highlightedText> prompt?",
          },
          {
            highlightedText: (text: ReactNode) => (
              <span className="bg-gradient-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
                {text}
              </span>
            ),
          }
        )}
        buttons={[
          {
            text: t({
              id: "common.cancel",
              defaultMessage: "Cancel",
            }),
            color: "grey",
            role: "close",
            style: { flex: "none", width: "140px" },
          },
          {
            text: t({
              id: "common.reset",
              defaultMessage: "Reset",
            }),
            color: "primary",
            iconEnd: <ArrowIcon />,
            onClick: (_, { closeAlert }) => {
              closeAlert();
              persistEscalateConfig(DEFAULT_ESCALATE_TO_HUMAN_MESSAGE);
            },
          },
        ]}
      />
    </div>
  );
};
